import {z} from "zod/v4-mini"
import type {
    AccordionStyle,
    BoundsEdgeNumber,
    ColorValue,
    ContrastToneMapping,
    CoronaToneMapping,
    DecalMaskType,
    EnvironmentMapMode,
    FilmicAdvancedToneMapping,
    FilmicToneMapping,
    HueSaturationMapping,
    LightType,
    LinearToneMapping,
    MaterialSide,
    PBRNeutralToneMapping,
    RGBCurveMapping,
    TextureResolution,
    ToneMapping,
    UiStyle,
    UnitForDisplay,
} from "#template/nodes/types"
import {isValidExternalId} from "#template/nodes/types"

export const textureResolution: z.ZodMiniType<TextureResolution, TextureResolution> = z.enum(["500px", "1000px", "2000px", "original"])
export const environmentMapMode: z.ZodMiniType<EnvironmentMapMode, EnvironmentMapMode> = z.enum(["full", "specularOnly"])
export const uiStyle: z.ZodMiniType<UiStyle, UiStyle> = z.enum(["default", "icons", "hidden", "accordion", "mobileTabs"])
export const accordionStyle: z.ZodMiniType<AccordionStyle, AccordionStyle> = z.enum(["autoClose", "manualClose", "stepInto", "stepThrough"])
export const decalMaskType: z.ZodMiniType<DecalMaskType, DecalMaskType> = z.enum(["binary", "opacity"])
export const materialSide: z.ZodMiniType<MaterialSide, MaterialSide> = z.enum(["front", "back", "double"])
export const lightType: z.ZodMiniType<LightType, LightType> = z.enum(["Corona", "Blender"])
export const unitForDisplayOptions = ["mm", "cm", "m", "in", "ft"] as const
export const unitForDisplay: z.ZodMiniType<UnitForDisplay, UnitForDisplay> = z.enum(unitForDisplayOptions)
export const boundsEdgeNumbers = [0, 1, 2, 3] as const
export const boundsEdgeNumber: z.ZodMiniType<BoundsEdgeNumber, BoundsEdgeNumber> = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)])

export const filmicToneMapping = z.object({mode: z.literal("filmic")}) satisfies z.ZodMiniType<FilmicToneMapping, FilmicToneMapping>
export const filmicAdvancedToneMapping = z.object({
    mode: z.literal("filmic-advanced"),
    contrast: z.number(),
    balance: z.number(),
    colorBalance: z.number(),
}) satisfies z.ZodMiniType<FilmicAdvancedToneMapping, FilmicAdvancedToneMapping>
export const contrastToneMapping = z.object({
    mode: z.literal("contrast"),
    contrast: z.number(),
    balance: z.number(),
    colorBalance: z.number(),
}) satisfies z.ZodMiniType<ContrastToneMapping, ContrastToneMapping>
export const coronaToneMapping = z.object({
    mode: z.literal("corona"),
    highlightCompression: z.number(),
    contrast: z.number(),
    saturation: z.number(),
}) satisfies z.ZodMiniType<CoronaToneMapping, CoronaToneMapping>
export const pbrNeutralToneMapping = z.object({mode: z.literal("pbr-neutral")}) satisfies z.ZodMiniType<PBRNeutralToneMapping, PBRNeutralToneMapping>
export const linearToneMapping = z.object({mode: z.literal("linear")}) satisfies z.ZodMiniType<LinearToneMapping, LinearToneMapping>
export const rgbCurveMapping: z.ZodMiniType<RGBCurveMapping, RGBCurveMapping> = z.custom<RGBCurveMapping>((value) => {
    const schema = z.object({
        mode: z.literal("rgbCurve"),
        parameters: z.record(z.string(), z.union([z.number(), z.tuple([z.number(), z.number()])])),
    })
    const isRgbCurveMapping = (value: {mode: "rgbCurve"; parameters: {[key: string]: [number, number] | number}}): value is RGBCurveMapping => {
        return typeof value.parameters.fac === "number"
    }
    return schema.safeParse(value).success && isRgbCurveMapping(value)
})
export const hueSaturationMapping = z.object({
    mode: z.literal("hueSaturation"),
    parameters: z.object({fac: z.number(), hue: z.number(), saturation: z.number(), value: z.number()}),
}) satisfies z.ZodMiniType<HueSaturationMapping, HueSaturationMapping>
export const toneMapping: z.ZodMiniType<ToneMapping, ToneMapping> = z.union([
    filmicToneMapping,
    contrastToneMapping,
    coronaToneMapping,
    pbrNeutralToneMapping,
    linearToneMapping,
    filmicAdvancedToneMapping,
    rgbCurveMapping,
    hueSaturationMapping,
])

const number01 = z.number().check(z.minimum(0), z.maximum(1))
export const colorValue: z.ZodMiniType<ColorValue, ColorValue> = z.tuple([number01, number01, number01])
export const isColorValue = (value: unknown): value is ColorValue => colorValue.safeParse(value).success

export const externalId = z.string().check(z.refine(isValidExternalId))

export const positionValue = z.tuple([z.number(), z.number(), z.number()])
export const eulerValue = z.tuple([z.number(), z.number(), z.number()])
export const vectorValue = z.tuple([z.number(), z.number(), z.number()])
export const uvValue = z.tuple([z.number(), z.number()])
export const matrix4Value = z.array(z.number()).check(z.length(16))
